// "My purchases" — the caller's own orders, newest first. The server returns only
// the caller's orders; recipients arrive already masked.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Smartphone, Wifi, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { formatMoney } from '@/lib/money';
import { getApiErrorMessage } from '@/lib/apiError';
import type { OperatorOption, Order } from '../types';
import { operatorName } from '../lib/format';
import { OrderStatusBadge } from './OrderStatusBadge';

export const ProductIcon: React.FC<{ type: string; className?: string }> = ({ type, className = 'h-4 w-4' }) => {
  if (type === 'ELECTRICITY') return <Zap className={className} aria-hidden="true" />;
  if (type === 'DATA_BUNDLE') return <Wifi className={className} aria-hidden="true" />;
  return <Smartphone className={className} aria-hidden="true" />;
};

export function sortNewestFirst(orders: Order[]): Order[] {
  return [...orders].sort((a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0));
}

interface Props {
  orders: Order[] | null;
  error: unknown;
  operators: OperatorOption[];
  onRetry: () => void;
  onOpen: (id: string) => void;
}

export const PurchasesList: React.FC<Props> = ({ orders, error, operators, onRetry, onOpen }) => {
  const { t, i18n } = useTranslation('everydayServices');

  let body: React.ReactNode;
  if (error) {
    body = (
      <DataState
        variant={errorVariant(error)}
        title={t('errors.orders')}
        message={getApiErrorMessage(error, t('errors.orders'))}
        onRetry={onRetry}
      />
    );
  } else if (orders === null) {
    body = <DataState variant="loading" />;
  } else if (orders.length === 0) {
    body = <DataState variant="empty" title={t('purchases.emptyTitle')} message={t('purchases.emptyMessage')} />;
  } else {
    body = (
      <ul className="divide-y divide-border">
        {sortNewestFirst(orders).map((o) => {
          const net = operatorName(o.operator, operators);
          const when = o.createdAt ? new Date(o.createdAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }) : '';
          return (
            <li key={o.id}>
              <button
                type="button"
                onClick={() => onOpen(o.id)}
                className="flex w-full items-center gap-3 px-1 py-3 text-left hover:bg-cream-dark rounded-lg transition-colors"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                  <ProductIcon type={o.productType} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {t(`products.${o.productType}`, o.productType)}{net ? ` · ${net}` : ''}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {o.recipient}{when ? ` · ${when}` : ''}
                  </span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="text-sm font-semibold tabular-nums text-foreground">
                    {formatMoney(o.customerTotal || o.faceValue, o.currency)}
                  </span>
                  <OrderStatusBadge status={o.status} paymentStatus={o.paymentStatus} />
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="sr-only">{t('purchases.view')}</span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('purchases.title')}</CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default PurchasesList;
